<template>
	<div class="approval-content">
		<FinanceButton
			class="px-5 py-3 d-print-none"
			:isPost="isPosted || !invalidAmount"
			:isActive="claim && claim.status !== post_status && claim.status !== deleted"
			:isDelete="claim && (claim.status === draft || claim.status === submitted || claim.status === approved || claim.status === rejected)"
			:isRecover="claim && claim.status === deleted && claim.status !== post_status && claim.status !== paid"
			:postLoading="postLoading"
			:rejectLoading="rejectLoading"
			:forwardLoading="forwardLoading"
			:deleteLoading="deleteLoading"
			:printLoading="printLoading"
			:recoverLoading="recoverLoading"
			:claimId="claimId"
			@post-gl="onPostGL()"
			@more-info="onMoreInfo()"
			@forward="onForward()"
			@print="onPrint()"
			@delete="onDelete()"
			@recover="onRecover()"
		/>
		<v-alert class="mx-5 claim-error d-print-none" type="error" v-if="error_msg.length !== 0">
			{{ error_msg }}
		</v-alert>
		<v-row class="mx-0 my-3 px-3" style="width: 100%">
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 2" class="claim-summary-field py-0">
				<div class="content-info-title">
					Submitted date
				</div>
				<v-text-field
					:value="formatDate(claim && claim.submitted_date)"
					outlined
					disabled
					dense
					hide-details
				>
					<template v-slot:prepend-inner>   
						<Calendar class="mr-1" style="margin-top: 2px" color="rgba(0, 0, 0, 0.38)" width="20" height="20"/>     
					</template>
				</v-text-field>
			</v-col>
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 2" class="claim-summary-field py-0">
				<div class="content-info-title">Claim date</div>
				<v-text-field
					v-if="claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r')"
					:value="formatDate(claim && claim.submitted_date)"
					outlined
					disabled
					dense
					hide-details
				>
					<template v-slot:prepend-inner>   
						<Calendar class="mr-1" style="margin-top: 2px" color="rgba(0, 0, 0, 0.38)" width="20" height="20"/>     
					</template>
				</v-text-field>
				<DatePicker
					v-if="claim && !claim.has_payment && claim.status !== deleted && !claim.number.includes('-r') && !claim.number.includes('*r')"
					:color="color"
					:background="`#FAFAFA`"
					:isOutlined="true"
					:isIcon="true"
					:lableText="''"
					:dateInit="claim_date_init"
					@date="onClaimDateChanged($event)"
				/>
			</v-col>
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 2" class="claim-summary-field py-0">
				<div class="content-info-title">Period</div>
				<v-select
					v-if="claim"
					class="mt-0 content-info-text content-info-default-color"
					v-model="period"
					:items="periodList"
					:color="color"
					item-color="blue"
					item-text="period_desc"
					item-value="id"
					:background-color="claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r') ? '#FFFFFF' :'#FAFAFA'"
					outlined
					return-object
					hide-details
					dense
					:disabled="claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r')"
					@change="onPeriodChanged()"
				/>
			</v-col>
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 2" class="claim-summary-field py-0">
				<div class="content-info-title">Currency</div>
				<v-select
					v-if="claim"
					class="mt-0 content-info-text content-info-default-color"
					v-model="currency"
					:items="currencies"
					:color="color"
					item-color="blue"
					item-text="code"
					item-value="id"
					:background-color="claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r') ? '#FFFFFF':'#FAFAFA'"
					return-object
					hide-details
					dense
					outlined
					:disabled="claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r')"
					@change="onCurrencyChanged()"
				/>
			</v-col>
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 2" class="claim-summary-field py-0">
				<div class="content-info-title">Amount</div>
				<v-text-field
					:value="`${currency && currency.symbol} ${formatNumber(gross)}`"
					outlined
					disabled
					dense
					hide-details
				>
				</v-text-field>
			</v-col>
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 2" class="claim-summary-field py-0">
				<div class="content-info-title">GST</div>
				<v-text-field
					:value="`${currency && currency.symbol} ${formatNumber(tax)}`"
					outlined
					disabled
					dense
					hide-details
				>
				</v-text-field>
			</v-col>
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 2" class="claim-summary-field py-0">
				<div class="content-info-title">To Pay</div>
				<v-text-field
					:value="`${currency && currency.symbol} ${formatNumber(amount)}`"
					outlined
					disabled
					dense
					hide-details
				>
				</v-text-field>
			</v-col>
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 4" class="claim-summary-field py-0">
				<div class="content-info-title">Claim description</div>
				<v-text-field 
					class="mt-0 content-info-text content-info-default-color"
					v-model="description" 
					placeholder="REQUIRED"
					:color="color"
					:background-color="claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r') ? '#FFFFFF':'#FAFAFA'"
					outlined
					hide-details
					dense
					:disabled="claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r')"
					@change="onDescChanged()"
				/>
			</v-col>
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 2" class="claim-summary-field py-0">
				<div class="content-info-title">Employee</div>
				<v-text-field
					:value="claim && claim.supplier.display_name"
					outlined
					disabled
					dense
					hide-details
				>
				</v-text-field>
			</v-col>
			<v-col :cols="$vuetify.breakpoint.xs ? 12: 2" class="claim-summary-field py-0">
				<div class="content-info-title">Approval by</div>
				<v-text-field
					:value="claim && claim.approval_by && claim.approval_by.display_name"
					outlined
					disabled
					dense
					hide-details
				>
				</v-text-field>
			</v-col>
		</v-row>
		<div class="attached-title mx-5 mt-5">Attached Expenses</div>
		<div class="content-attached-title px-5 py-2 d-print-none">
			<v-btn background-color="#FAFAFA" class="mr-4 text-capitalize" :disabled="claim && (claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r'))" @click="onSplit()">
				<CopyIcon :color="isSelected ? 'rgba(25, 42, 62, 0.7)':'rgba(0, 0, 0, 0.26)'" width="16" height="16" class="mr-1"/>
				<span :style="isSelected ? 'color:rgba(25, 42, 62, 0.7)':'color:rgba(0, 0, 0, 0.26)'">Split</span>
			</v-btn>
			<v-btn background-color="#FAFAFA" class="text-capitalize" :disabled="claim && (claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r'))" @click="onDeleteReceipt()">
				<DeleteIcon style="margin-top: 4px" :color="isSelected ? 'rgba(25, 42, 62, 0.7)':'rgba(0, 0, 0, 0.26)'" width="20" height="20" class="mr-1"/>
				<span :style="isSelected ? 'color:rgba(25, 42, 62, 0.7)': 'color:rgba(0, 0, 0, 0.26)'">Delete</span>
			</v-btn>
		</div>
		<TableAttachedReceipt 
			class="content-table mt-2 mb-5"
			:header="claim && (claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r')) ? head_local.filter(h => { return h.value !== 'select' }) : head_local"
			:items="filterResults"
			:color="color" 
			:currencies="currencies"
			:tax_settings="tax_settings"
			:symbol="currency && currency.symbol"
			:amount="formatNumber(amount)"
			:gross="formatNumber(gross)"
			:tax="formatNumber(tax)"
			:markup="formatNumber(markupSum)"
			:isFile="true"
			:isFileView="claim && (claim.has_payment || claim.status === deleted || claim.number.includes('-r') || claim.number.includes('*r'))"
			@row-click="onRowClick($event)"
			@selected="isSelected = $event"
			@show-file="onShowFile($event)"
		/>
		<ReceiptImageView 
			v-if="image_visible"
			:visible="image_visible" 
			:show_image="show_image"
			@image-close="image_visible = false"
		/>
	</div>
</template>
<script>
import TableAttachedReceipt from "@/components/table/table_attached_receipt.vue";
import FinanceButton from "@/views/expenses/finance/finance_button.vue";
import CopyIcon from '@/components/icons/copy';
import DeleteIcon from '@/components/icons/delete_outline';
import DatePicker from "@/components/date/date_picker.vue";
import ReceiptImageView from "@/views/expenses/receipts/receipt_image_view.vue";
import Calendar from "@/components/icons/calendar.vue";

import config from "@/config.js";
import moment from 'moment';

const HEAD = config.finance_receipt_head;
const CLAIM_STATUSES = config.claim_statuses;

export default {
	name: 'FinanceClaimContent',
	props: {
		color: {
			type: String,
			default: ""
		},
		currencies: {
			type: Array,
			default: () => [],
		},
		error_message: {
			type: String,
			default: ""
		},
		claim: {
			type: Object,
			default: () => {}
		},
		isPosted: {
			type: Boolean,
			default: false
		},
		postLoading: {
			type: Boolean,
			default: false
		},
		rejectLoading: {
			type: Boolean,
			default: false
		},
		forwardLoading: {
			type: Boolean,
			default: false
		},
		deleteLoading: {
			type: Boolean,
			default: false
		},
		printLoading: {
			type: Boolean,
			default: false
		},
		recoverLoading: {
			type: Boolean,
			default: false
		},
		claimId: {
			type: Number,
			default: 0
		}
	},
	components: {
		FinanceButton,
		TableAttachedReceipt,
		CopyIcon,
		DeleteIcon,
		DatePicker,
		ReceiptImageView,
		Calendar
	},
	data() {
		this.draft = CLAIM_STATUSES.draft.value;
		this.submitted = CLAIM_STATUSES.pending.value;
		this.approved = CLAIM_STATUSES.approved.value;
		this.rejected = CLAIM_STATUSES.rejected.value;
		this.deleted = CLAIM_STATUSES.deleted.value;
		this.paid = CLAIM_STATUSES.paid.value;
		
		return {
			error_msg: '',
			isSelected: false,
			head_local: HEAD,
			tax_settings: config.tax_settings,

			claim_date_init: this.claim.date,
			claim_date: this.claim.date,
			description: this.claim.description,
			currency: this.claim.currency,
			details:  this.claim.details,
			period: this.claim.period,
			period_origin: this.claim.period,
			amount: '0.00',
			gross: '0.00',
			tax: '0.00',
			markup: '0.00',
			totals_calculation_id: 0,
			
			period_options: [],
			entry_period: null,
			post_status: CLAIM_STATUSES.posted.value,
			image_visible: false,
		}
	},
	created() {
		this.getEntryPeriod();
		this.getPeriod();
		this.onReCalculate();
	},
	watch: {
		claim() {
			if (this.claim) {
				this.claim_date_init = this.claim.date;
				this.claim_date = this.claim.date;
				this.description = this.claim.description;
				this.currency = this.claim.currency;
				this.details = this.claim.details;
				this.period = this.claim.period;
				this.period_origin = this.claim.period;
			}
		},
		error_message() {
			this.error_msg = this.error_message;
		},
		claim_date() {
			if (this.claim_date) {
				this.onReCalculate();
			}
		},
		async currency() {
			this.onReCalculate();
		},
		details: {
			handler: function(val, oldVal) { 
				if (oldVal.length !== 0) {
					this.onReCalculate();
				}
			},
			deep: true
		},
	},
	computed: {
		filterResults() {
			const filterRes = [];
			this.details.forEach(detail => {
				const obj = {};

				/* from claim's details */
				for (const [key, value] of Object.entries(detail)) {
					if (key === 'job') {
						obj['code'] = value;
						obj['title'] = value;
					} else if (key === 'receipt') {
						obj['receipt_id'] = value.id;
						obj['currency'] = value.currency;
						obj['date'] = value.date;
						obj['has_tax'] = value.has_tax;
						obj['image'] = value.image;
						obj['image_type'] = value.image_type;
						obj['thumbnail'] = value.thumbnail;
						obj['has_tax'] = value.has_tax;
						obj['gross'] = Number(value.gross);
						obj['tax_amount'] = Number(value.tax_amount);
						obj['tax_perc'] = Number(value.tax_perc);
						const tax_mode = Number(value.taxmode);
						const tax_setting = config.tax_settings.find(setting => setting.value === tax_mode);
						obj['tax_mode'] = tax_mode;
						obj['tax_mode_code'] = tax_mode;
						obj['tax_mode_desc'] = tax_setting ? tax_setting.text : '';
						obj['markup_perc'] = Number(Number(value.markup).toFixed(2));
						obj['markup_value'] = Number(value.value);
						obj['amount'] = Number((Number(value.gross) + Number(value.tax_amount)).toFixed(2));
					}  else if (key === 'kind') {
						obj[`${key}`] = value;
					} else if (key === 'phase') {
						obj[`${key}`] = value;
					} else if (key === 'description') {
						obj[`${key}`] = value;
					} 
				}

				obj['id'] = detail.id;
				obj['is_new_split'] = detail.is_new_split === true;
				obj['select'] = false;
				obj['origin_currency'] = detail.receipt.currency;
				obj['origin_value'] = this.formatNumber(detail.receipt.value);
				obj['employee'] = this.claim.supplier;

				filterRes.push(obj);
			});
			return filterRes;
		},
		periodList() {
			let periodList_tmp = [];

			if (this.period_options.length !==0 && this.entry_period) {
				const array_1 = [this.period_options[0]];
				const array_2 = [this.entry_period];
				const array_3 = this.period_options.slice(1);
				periodList_tmp = [...array_1, ...array_2, ...array_3];
			}
			
			return periodList_tmp;
		},
		markupSum() {
			return Number((Number(this.markup) || 0).toFixed(2));
		},
		invalidAmount() {
			return !this.details.some(detail => Number(detail.gross) === 0);
		}
	},
	methods:{
		getPeriod() {			
			const params = [];
			params.push('include_current=' + true);
			params.push('include_future=' + true);
			params.push('include_past=' + false);
			params.push('periods=' + `${this.claim.period.id}`);

			let url = "/api/config/periods";
			if (params.length > 0) {
				url+= "?" + params.join('&');
			}
			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then(val => {
					const {status, data, reason} = val.data;

					if (status === "OK") {
						const options_current_tmp = data.filter(d => { return d.is_current === true });
						options_current_tmp.forEach(o => {o.period_desc = `Current period - ${o.period_desc}`});

						const options_tmp = data.filter(d => { return d.is_current === false });
						options_tmp.sort((a,b) => {return a.id - b.id});
						this.period_options = [...options_current_tmp, ...options_tmp];
					} else {
						this.error_msg = reason;
					}
				})
				.catch(e => {
					this.error_msg = e.message;
				})
		},
		getEntryPeriod() {
			let url = "/api/config/periods/entry_period";
			
			axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then(val => {
					const {status, data, reason} = val.data;

					if (status === "OK") {
						data.period_desc = `Entry period - ${data.period_desc}`
						this.entry_period = data;
					} else {
						this.error_msg = reason;
					}
				})
				.catch(e => {
					this.error_msg = e.message;
				})
		},
		formatDate(value) {
			if(!value) return null;
			return moment(value).format('DD MMM YYYY');
		},
		formatNumber(num) {
			let res = 0;
			if (Number.isInteger(num)) {
				const integer = num.toLocaleString();
				res = integer + '.00';
			} else {
				const num_tmp = typeof(num) === 'string'? num : num.toString();
				const num_arr = num_tmp.split('.');
				const integer = Number(num_arr[0]).toLocaleString();

				if (num_arr.length > 1) {
					if (num_arr[1].length === 1) {
						res = integer + '.' + num_arr[1] + '0';
					} else {
						res = integer + '.' + num_arr[1];
					}
					
				} else {
					res = integer + '.00' ;
				}
			}
			return res;
		},
		onRowClick(detail) {
			if (!(this.claim.has_payment || this.claim.status === this.deleted || this.claim.number.includes('-r') || this.claim.number.includes('*r'))) {
				this.$emit('row-click', detail);
			}
		},
		onShowFile(show_image) {
			if (this.claim.has_payment || this.claim.status === this.deleted || this.claim.number.includes('-r') || this.claim.number.includes('*r')) {
				this.show_image = show_image;
				this.image_visible = true;
			}
		},
		onClaimDateChanged(date) {
			if (!date) return;
			this.claim_date = date;
			this.$emit('date-change', date);
		},
		onPeriodChanged() {
			let claim_date = '';

			if (this.period_origin.start_date > this.period.start_date) {
				claim_date = this.period.end_date;
			} else {
				claim_date = this.period.start_date;
			}

			this.$emit('period-change', {period: this.period, date: claim_date});
		},
		onDescChanged() {
			this.$emit('desc-change', this.description);
		},
		onCurrencyChanged() {
			this.$emit('currency-change', this.currency);
		},
		onDeleteReceipt() {
			if (!this.isSelected) return;

			const arr_tmp =  this.filterResults.filter(res => { return res.select === true });
			const pre_delete_ids = arr_tmp
				.map(detail => detail.receipt_id)
				.filter(receipt_id => Number(receipt_id) > 0);
			const temporary_ids = new Set(
				arr_tmp
					.filter(detail => Number(detail.id) < 0)
					.map(detail => detail.id)
			);

			if (temporary_ids.size > 0) {
				this.details = this.details.filter(detail => !temporary_ids.has(detail.id));
			}

			if (pre_delete_ids.length > 0) {
				this.$emit('detail-delete', pre_delete_ids);
			}

			this.isSelected = false;
		},
		onSplit() {
			if (!this.isSelected) return;

			const selected_detail_ids = new Set(
				this.filterResults
					.filter(detail => detail.select === true)
					.map(detail => detail.id)
			);
			const details_with_splits = [];
			const split_details = [];
			let temporary_id = this.details.reduce((lowest_id, detail) => {
				return Math.min(lowest_id, Number(detail.id) || 0);
			}, 0) - 1;

			this.details.forEach(detail => {
				details_with_splits.push(detail);

				if (selected_detail_ids.has(detail.id)) {
					const tax_mode = detail.receipt.taxmode !== undefined
						? Number(detail.receipt.taxmode)
						: Number(detail.tax_mode);
					const tax_perc = detail.receipt.tax_perc !== undefined
						? Number(detail.receipt.tax_perc)
						: Number(detail.tax_perc);
					const has_tax_value = detail.receipt.has_tax !== undefined
						? detail.receipt.has_tax
						: detail.has_tax;
					const has_tax = has_tax_value === true || Number(has_tax_value) === 1;
					const split_detail = Object.assign({}, detail, {
						id: temporary_id,
						is_new_split: true,
						gross: 0,
						tax_mode: tax_mode,
						tax_mode_desc: config.tax_settings.find(setting => setting.value === tax_mode).text,
						tax_amount: 0,
						tax_perc: tax_perc,
						amount: 0,
						markup: detail.markup !== undefined ? detail.markup : detail.markup_perc,
						value_fc: 0,
						has_tax: has_tax,
						receipt: Object.assign({}, detail.receipt, {
							has_tax: has_tax,
							taxmode: tax_mode,
							tax_perc: tax_perc,
							image: '',
							image_type: '',
							thumbnail: ''
						})
					});

					details_with_splits.push(split_detail);
					split_details.push(split_detail);
					temporary_id -= 1;
				}
			});

			this.details = details_with_splits;
			this.isSelected = false;

			if (split_details.some(detail => Number(detail.id) < 0)) {
				this.$emit('detail-split', details_with_splits);
			}
		},
		onPrint() {
			if (this.printLoading) return;

			this.$emit('print',this.amount);
		},
		async onPostGL() {
			if (this.postLoading) return;
			
			if (!this.isPosted) {
				this.$emit('post-gl');
			}
		},
		async onMoreInfo() {
			if (this.rejectLoading) return;

			if (this.claim.status !== this.post_status && this.claim.status !== this.deleted && !this.claim.number.includes('-r') && !this.claim.number.includes('*r')) {
				this.$emit('more-info');
			}
		},
		async onForward() {
			if (this.forwardLoading) return;

			if (this.claim.status !== this.post_status && this.claim.status !== this.deleted && !this.claim.number.includes('-r') && !this.claim.number.includes('*r')) {
				this.$emit('forward');
			}
		},
		onDelete() {
			if (this.deleteLoading) return;

			if (this.claim.status === this.draft || this.claim.status === this.submitted || this.claim.status === this.approved || this.claim.status === this.rejected) {
				this.$emit('delete');
			}
		},
		onRecover() {
			if (this.recoverLoading) return;

			if (this.claim.status === this.deleted && this.claim.status !== this.post_status && this.claim.status !== this.paid) {
				this.$emit('recover');
			}
		},
		async onReCalculate() {
			const calculation_id = this.totals_calculation_id + 1;
			this.totals_calculation_id = calculation_id;

			const values = this.details.map(detail => {
				const receipt = detail.receipt;
				const amount = Number(receipt.gross) || 0;
				const gst = Number(receipt.tax_amount) || 0;

				return {
					currency_id: receipt.currency.id,
					date: receipt.date,
					amount: amount,
					gst: gst,
					to_pay: this._add(amount, gst),
					value: Number(receipt.value) || 0
				};
			});

			if (!this.currency || values.length === 0) {
				this.amount = '0.00';
				this.gross = '0.00';
				this.tax = '0.00';
				this.markup = '0.00';
				return;
			}

			const requires_exchange = values.some(value => value.currency_id !== this.currency.id);
			const rate = requires_exchange
				? Number(await this._get_exchange_rate(this.currency.id, this.claim_date))
				: 1;
			const rate_cache = {};
			const get_origin_rate = (currency_id, date) => {
				const key = `${currency_id}_${date}`;

				if (!rate_cache[key]) {
					rate_cache[key] = this._get_exchange_rate(currency_id, date);
				}

				return rate_cache[key];
			};

			await Promise.all(values.map(async value => {
				if (value.currency_id === this.currency.id) return;

				const origin_rate = Number(await get_origin_rate(value.currency_id, value.date));
				this.exchange_rate(value, rate, origin_rate);
			}));

			if (calculation_id !== this.totals_calculation_id) return;

			const total = key => values.reduce((sum, item) => {
				return this._add(sum, Number(item[key]) || 0);
			}, 0);

			this.gross = total('amount').toFixed(2);
			this.tax = total('gst').toFixed(2);
			this.amount = total('to_pay').toFixed(2);
			this.markup = total('value').toFixed(2);
		},
		exchange_rate(origin, rate, origin_rate) {
			const exchanged = origin;
			if (origin.currency_id !== this.currency.id) {
				const multiplier = Number(rate) / Number(origin_rate);
				['amount', 'gst', 'to_pay', 'value'].forEach(key => {
					exchanged[key] = Number((Number(origin[key]) * multiplier).toFixed(2));
				});
			} 
			return exchanged;
		},
		_add(num1, num2) {
			const num1Digits = (num1.toString().split('.')[1] || '').length;
			const num2Digits = (num2.toString().split('.')[1] || '').length;
			const baseNum = Math.pow(10, Math.max(num1Digits, num2Digits));
			return (num1 * baseNum + num2 * baseNum) / baseNum;
		},
		async _get_exchange_rate(currency_id, date) {
			let rate = null;
			const url = "/api/config/currency/" + currency_id + "/exchange_rates/" + date;

			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then(val => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						rate = data.rate;
					} else {
						this.error_msg = reason;
					}					
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
			
			return rate;
		},
	}
}
</script>
<style lang="scss" scoped>
.approval-content {
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
	background: #FFFFFF;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
}
.approval-content-button {
	display: flex;
  flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
}
.content-info {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  width: 100%;
}
.content-info-xs {
  display: flex;
  flex-flow: column nowrap;
	align-items: flex-start;
  width: 100%;
}
.claim-summary-field {
	min-width: 240px;
}
.content-info-title {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 36px;
}
.content-info-text {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
}
.content-info-default-color {
  // color: #37495F
	color: #192A3E;
}
.content-info-special-color {
  color: #EE0202;
}
.attached-title {
	font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 32px;
  color: #192A3E;
}
.content-attached-title {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: #192A3E;
}
.content-table {
  width: 100%;
}
.claim-error {
	width: 98%;
}
.button-text-disable {
	color: rgba(0, 0, 0, 0.26);
}
.button-text-able {
	color: #FFFFFF;
}

</style>
